import axios from "axios";

const configuredBackendUrl = process.env.REACT_APP_BACKEND_URL;
const BACKEND_URL =
  configuredBackendUrl && configuredBackendUrl !== "undefined"
    ? configuredBackendUrl.replace(/\/+$/, "")
    : "";
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "nelum_admin_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      const p = window.location.pathname;
      if (p.startsWith("/admin") && p !== "/admin/login") {
        clearToken();
        window.location.href = "/admin/login";
      }
    }
    return Promise.reject(err);
  }
);

// ==================== Public form APIs ====================
export const createReservation = (payload) =>
  api.post("/reservations", payload).then((r) => r.data);

export const createContactMessage = (payload) =>
  api.post("/contact", payload).then((r) => r.data);

// ==================== Auth ====================
export const adminLogin = (email, password) =>
  api.post("/auth/login", { email, password }).then((r) => r.data);

export const adminMe = () => api.get("/auth/me").then((r) => r.data);

export const adminLogout = () => api.post("/auth/logout").then((r) => r.data);

// ==================== Menus — public ====================
export const fetchMenuTree = ({ includeHidden = false } = {}) =>
  api.get(`/menus${includeHidden ? "?include_hidden=true" : ""}`).then((r) => r.data);

// ==================== Menus — admin ====================
export const createGroup = (payload) => api.post("/menus/groups", payload).then((r) => r.data);
export const updateGroup = (id, payload) => api.patch(`/menus/groups/${id}`, payload).then((r) => r.data);
export const deleteGroup = (id) => api.delete(`/menus/groups/${id}`).then((r) => r.data);

export const createSection = (payload) => api.post("/menus/sections", payload).then((r) => r.data);
export const updateSection = (id, payload) => api.patch(`/menus/sections/${id}`, payload).then((r) => r.data);
export const deleteSection = (id) => api.delete(`/menus/sections/${id}`).then((r) => r.data);

export const createItem = (payload) => api.post("/menus/items", payload).then((r) => r.data);
export const updateItem = (id, payload) => api.patch(`/menus/items/${id}`, payload).then((r) => r.data);
export const deleteItem = (id) => api.delete(`/menus/items/${id}`).then((r) => r.data);

// ==================== File uploads ====================
export const uploadImage = (file, onUploadProgress) => {
  const form = new FormData();
  form.append("file", file);
  return api
    .post("/uploads/image", form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress,
    })
    .then((r) => r.data);
};

// Accepts image OR video for hero
export const uploadMedia = (file, onUploadProgress) => {
  const form = new FormData();
  form.append("file", file);
  return api
    .post("/uploads/media", form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress,
    })
    .then((r) => r.data);
};

export const uploadMenuPdf = (file, onUploadProgress) => {
  const form = new FormData();
  form.append("file", file);
  return api
    .post("/uploads/menu-pdf", form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress,
    })
    .then((r) => r.data);
};

// ==================== Hero media ====================
// ==================== Site content ====================
export const listSiteContent = () => api.get("/site-content").then((r) => r.data);
export const getSiteContent = (section) => api.get(`/site-content/${section}`).then((r) => r.data);
export const updateSiteContent = (section, payload) =>
  api.patch(`/site-content/${section}`, payload).then((r) => r.data);

// ==================== Site settings ====================
export const getSiteSettings = () => api.get("/site-settings").then((r) => r.data);
export const updateSiteSettings = (payload) =>
  api.patch("/site-settings", payload).then((r) => r.data);

export const uploadBranding = (kind, file) => {
  const form = new FormData();
  form.append("file", file);
  return api
    .post(`/uploads/branding?kind=${encodeURIComponent(kind)}`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((r) => r.data);
};


// listHeroMedia(activeOnly, page, device)
export const listHeroMedia = (activeOnly = false, page = null, device = null) => {
  const params = new URLSearchParams();
  if (activeOnly) params.set("active_only", "true");
  if (page) params.set("page", page);
  if (device) params.set("device", device);
  const qs = params.toString();
  return api.get(`/hero-media${qs ? `?${qs}` : ""}`).then((r) => r.data);
};
export const createHeroMedia = (payload) => api.post("/hero-media", payload).then((r) => r.data);
export const updateHeroMedia = (id, payload) => api.patch(`/hero-media/${id}`, payload).then((r) => r.data);
export const deleteHeroMedia = (id) => api.delete(`/hero-media/${id}`).then((r) => r.data);

// Build a fully-qualified URL for a returned relative image_url (e.g. "/api/files/...")
export const absoluteUrl = (relPath) => {
  if (!relPath) return null;
  if (/^https?:\/\//i.test(relPath)) return relPath;
  return `${BACKEND_URL}${relPath}`;
};

// ==================== Reservations (admin) ====================
export const listReservations = () => api.get("/reservations").then((r) => r.data);
export const updateReservationStatus = (id, status) =>
  api.patch(`/reservations/${id}`, { status }).then((r) => r.data);
export const deleteReservation = (id) => api.delete(`/reservations/${id}`).then((r) => r.data);

// ==================== Contact Messages (admin) ====================
export const listContactMessages = () => api.get("/contact").then((r) => r.data);
export const updateContactMessage = (id, payload) =>
  api.patch(`/contact/${id}`, payload).then((r) => r.data);
export const deleteContactMessage = (id) => api.delete(`/contact/${id}`).then((r) => r.data);

// ==================== Users (admin only) ====================
export const listUsers = () => api.get("/users").then((r) => r.data);
export const createUser = (payload) => api.post("/users", payload).then((r) => r.data);
export const updateUser = (id, payload) => api.patch(`/users/${id}`, payload).then((r) => r.data);
export const deleteUser = (id) => api.delete(`/users/${id}`).then((r) => r.data);

// ==================== Changelog ====================
export const getChangelog = () => api.get("/changelog").then((r) => r.data);
